import {
    getNonCancelledShipments,
    PartyExternalIdentifiers,
    Shipment,
    TransportOrder,
    TransportOrderStatus,
    TransportOrderType,
} from "@/model/transportorder/transportOrder"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { getEarliestLoadingTimeWindow } from "@/components/TransportOrder/TransportOrderTable/transportOrderTableData"
import queryString from "query-string"
import { getFilterFromQueryString, getFirstValueFromQueryString } from "@/helper/queryStringHelper"

export enum SortDirection {
    ASC = "asc",
    DESC = "desc"
}

export enum SortBy {
    CREATED_AT = "CREATED_AT",
    EARLIEST_LOADING_TIME_FROM = "EARLIEST_LOADING_TIME_FROM",
    CUSTOMER_NAME = "CUSTOMER_NAME"
}

export interface SortCriteria {
    sortBy: SortBy
    sortDirection: SortDirection
}

export enum TransportOrderDateFilterType {
    CREATED_AT = "created_at",
    EARLIEST_PICKUP_AT = "earliest_pickup_at",
    LATEST_DELIVERY_AT = "latest_delivery_at",
}

export enum CargoTypeFilter {
    ALL = "ALL",
    MATERIAL = "MATERIAL",
    EMPTIES = "EMPTIES",
}

export enum TransportOrderStatusFilter {
    ALL = "ALL",
    CONFIRMED = "CONFIRMED",
    UNCONFIRMED = "UNCONFIRMED",
}

interface TransportOrderWithKeywords {
    transportOrder: TransportOrder
    keywords: string[]
}

interface TransportOrderTableParameters {
    searchPhrase: string | undefined
    sortBy: SortBy
    sortDirection: SortDirection
    orderDateFrom: string | undefined
    orderDateTo: string | undefined
    dateFilterType: TransportOrderDateFilterType
    cargoTypeFilter: CargoTypeFilter
    statusFilter: TransportOrderStatusFilter
}

export const queryStringToSortDirectionMapping = new Map()
queryStringToSortDirectionMapping.set("asc", SortDirection.ASC)
queryStringToSortDirectionMapping.set("desc", SortDirection.DESC)

const mapQueryStringToSortDirection = (sortDirAsString: string | undefined): SortDirection => queryStringToSortDirectionMapping.get(sortDirAsString) || SortDirection.ASC

const dateFilterTypes = [
    TransportOrderDateFilterType.CREATED_AT,
    TransportOrderDateFilterType.EARLIEST_PICKUP_AT,
    TransportOrderDateFilterType.LATEST_DELIVERY_AT
]

const mapQueryStringToDateFilterType = (value: string | undefined): TransportOrderDateFilterType => dateFilterTypes.find(type => type === value) ?? TransportOrderDateFilterType.CREATED_AT

const getSortKey = (sortBy: SortBy, transportOrder: TransportOrder): string | undefined => {
    switch (sortBy) {
    case SortBy.CREATED_AT:
        return transportOrder.created_at
    case SortBy.EARLIEST_LOADING_TIME_FROM:
        return getEarliestLoadingTimeWindow(transportOrder)
    case SortBy.CUSTOMER_NAME:
        return transportOrder.customer_data?.name
    }
}

const queryStringToSortByMapping = (): Map<string | undefined, SortBy> => {
    const mapping = new Map()
    mapping.set("date", SortBy.CREATED_AT)
    mapping.set("loadingDate", SortBy.EARLIEST_LOADING_TIME_FROM)
    mapping.set("customer", SortBy.CUSTOMER_NAME)
    return mapping
}

const sortByToQueryStringMapping = (): Map<SortBy | undefined, string> => {
    const mapping = new Map()
    queryStringToSortByMapping().forEach((value, key) => {
        mapping.set(value, key)
    })
    return mapping
}

const mapQueryStringToSortBy = (sortByAsString: string | undefined): SortBy => queryStringToSortByMapping().get(sortByAsString) || SortBy.EARLIEST_LOADING_TIME_FROM

export const mapSortByToQueryString = (sortBy: SortBy | undefined): string | undefined => sortByToQueryStringMapping().get(sortBy) || ""

export const sortTransportOrders = (transportOrders: TransportOrder[],
    sortCriteria: SortCriteria | undefined): TransportOrder[] => {
    if (!sortCriteria) {
        return transportOrders
    }
    return doSorting(transportOrders, sortCriteria)
}

function doSorting(transportOrders: TransportOrder[], criteria: SortCriteria): TransportOrder[] {
    const orders = [...transportOrders]
    orders.sort((item1, item2) => {
        const sortKey1 = getSortKey(criteria.sortBy, item1)
        const sortKey2 = getSortKey(criteria.sortBy, item2)
        if (sortKey1 === undefined && sortKey2 === undefined) {
            return 0
        } else if (sortKey1 === undefined) {
            return criteria.sortDirection === SortDirection.ASC ? 1 : -1
        } else if (sortKey2 === undefined) {
            return criteria.sortDirection === SortDirection.ASC ? -1 : 1
        } else {
            const number = sortKey1.localeCompare(sortKey2)
            return criteria.sortDirection === SortDirection.ASC ? number : -number
        }
    })
    return orders
}

const keywordsFromShipments = (shipments: Shipment[] | undefined): string[] => {
    if (shipments) {
        return shipments
            .flatMap(it => [
                it.currentTransportLeg.placeOfDeparture.name,
                it.currentTransportLeg.placeOfDeparture.address?.city,
                it.currentTransportLeg.placeOfDeparture.address?.street,
                it.currentTransportLeg.placeOfArrival.name,
                it.currentTransportLeg.placeOfArrival.address?.city,
                it.currentTransportLeg.placeOfArrival.address?.street,
                ...keywordsFromShipmentPartyIdentifiers(it),
            ])
            .filter(it => it !== undefined)
    }
    return []
}

const keywordsFromPartyIdentifiers = (identifiers: PartyExternalIdentifiers | undefined): string[] => [
    identifiers?.plantCode,
    identifiers?.duns,
    identifiers?.supplierCode,
].filter((it): it is string => it !== undefined)

const keywordsFromShipmentPartyIdentifiers = (shipment: Shipment): string[] => [
    shipment.startOfTransportChain.consignor.externalIdentifiers,
    shipment.endOfTransportChain.consignee.externalIdentifiers,
    shipment.currentTransportLeg.placeOfDeparture.externalIdentifiers,
    shipment.currentTransportLeg.placeOfArrival.externalIdentifiers,
].flatMap(keywordsFromPartyIdentifiers)

const keywordsFromIdentifiers = (transportOrder: TransportOrder): string[] => {
    return [
        transportOrder.external_bundle_id,
        ...(transportOrder._embedded?.shipments ?? []).map(shipment => shipment.external_identification)
    ].filter((it): it is string => it !== undefined)
}

const withKeywords = (transportOrder: TransportOrder): TransportOrderWithKeywords => ({
    transportOrder,
    keywords: [
        transportOrder.customer_data?.name,
        ...keywordsFromIdentifiers(transportOrder),
        ...keywordsFromShipments(transportOrder._embedded?.shipments)
    ]
        .filter((it): it is string => it !== undefined)
})
// See https://stackoverflow.com/questions/4031900/split-a-string-by-whitespace-keeping-quoted-segments-allowing-escaped-quotes
export const requiredKeywordsFromSearchString = (searchString: string): string[] => {
    const tokens = searchString.match(/[^"\s]+|"(?:[^"])+"/g) || []
    return tokens
        .map(token => token.replace(/["]+/g, "").replace(/^,+|,+$/g, ""))
        .filter(token => token !== "")
}
export const getFilterRuleBySearchPhrase = (
    searchString: string | undefined
): ((transportOrder: TransportOrder) => boolean) => {
    if (!searchString || searchString === "") {
        return () => true
    }

    const requiredKeywords = requiredKeywordsFromSearchString(searchString)

    return (transportOrder) => {
        const transportOrderWithKeywords = withKeywords(transportOrder)
        return requiredKeywords.every(requiredKeyword => {
            return transportOrderWithKeywords.keywords.some(
                keyword => keyword.toLowerCase().indexOf(requiredKeyword.toLowerCase()) >= 0
            )
        })
    }
}

export const getFilterRuleByCargoType = (
    cargoTypeFilter: CargoTypeFilter
): ((transportOrder: TransportOrder) => boolean) => {
    switch (cargoTypeFilter) {
    case CargoTypeFilter.MATERIAL:
        return transportOrder => transportOrder.orderType === TransportOrderType.MATERIAL
    case CargoTypeFilter.EMPTIES:
        return transportOrder => transportOrder.orderType === TransportOrderType.EMPTIES
    default:
        return () => true
    }
}

export const getFilterRuleByTransportOrderStatus = (
    statusFilter: TransportOrderStatusFilter,
): ((transportOrder: TransportOrder) => boolean) => {
    switch (statusFilter) {
    case TransportOrderStatusFilter.CONFIRMED:
        return (transportOrder: TransportOrder) => {
            const activeShipments = getNonCancelledShipments(transportOrder)
            return transportOrder.status !== TransportOrderStatus.CANCELLED
                && activeShipments.length > 0
                && activeShipments.every(shipment => shipment.isConfirmed)
        }

    case TransportOrderStatusFilter.UNCONFIRMED:
        return (transportOrder: TransportOrder) => transportOrder.status !== TransportOrderStatus.CANCELLED
            && getNonCancelledShipments(transportOrder).some(shipment => !shipment.isConfirmed)

    default:
        return () => true
    }
}

export const getOverviewTransportOrders = (
    transportOrders: TransportOrder[],
    searchPhrase: string | undefined,
    cargoTypeFilter: CargoTypeFilter,
    statusFilter: TransportOrderStatusFilter,
    sortCriteria: SortCriteria | undefined
): TransportOrder[] => {
    const filteringRules: Array<(transportOrder: TransportOrder) => boolean> = [
        getFilterRuleByCargoType(cargoTypeFilter),
        getFilterRuleByTransportOrderStatus(statusFilter),
        getFilterRuleBySearchPhrase(searchPhrase),
    ]
    const filteredTransportOrders = transportOrders.filter(transportOrder =>
        filteringRules.every(rule => rule(transportOrder))
    )
    return sortTransportOrders(filteredTransportOrders, sortCriteria)
}

export const extractTableParamsFromUrl = (locationSearch: string): TransportOrderTableParameters => {
    const urlQueryParams = queryString.parse(locationSearch.replace(/^\?/, ""), { decode: true })
    const searchPhrase = Array.isArray(urlQueryParams["searchPhrase"]) ?
        urlQueryParams["searchPhrase"]?.join(" ") : urlQueryParams["searchPhrase"]
    const sortBy = getFirstValueFromQueryString(urlQueryParams["sortBy"])
    const sortDir = getFirstValueFromQueryString(urlQueryParams["sortDir"])
    const orderDateFrom = getFirstValueFromQueryString(urlQueryParams["orderDateFrom"])
    const orderDateTo = getFirstValueFromQueryString(urlQueryParams["orderDateTo"])
    const dateFilterType = getFirstValueFromQueryString(urlQueryParams["dateFilterType"])
    const cargoTypeFilter = getFilterFromQueryString(
        urlQueryParams["cargoTypes"], Object.values(CargoTypeFilter), CargoTypeFilter.ALL
    )
    const statusFilter = getFilterFromQueryString(
        urlQueryParams["statusFilter"], Object.values(TransportOrderStatusFilter), TransportOrderStatusFilter.ALL
    )
    return {
        searchPhrase: searchPhrase || undefined,
        sortBy: mapQueryStringToSortBy(sortBy || undefined),
        sortDirection: mapQueryStringToSortDirection(sortDir || undefined),
        orderDateFrom: orderDateFrom || undefined,
        orderDateTo: orderDateTo || undefined,
        dateFilterType: mapQueryStringToDateFilterType(dateFilterType || undefined),
        cargoTypeFilter,
        statusFilter,
    }
}

export interface TransportOrderState {
    transportOrders: TransportOrder[]
    overviewTransportOrders: TransportOrder[]
    failedFetchTransportOrderId?: string
    searchPhrase: string | undefined
    cargoTypeFilter: CargoTypeFilter
    statusFilter: TransportOrderStatusFilter
    sortCriteria: SortCriteria | undefined
}

export const initialState: TransportOrderState = {
    transportOrders: [],
    overviewTransportOrders: [],
    failedFetchTransportOrderId: undefined,
    searchPhrase: undefined,
    cargoTypeFilter: CargoTypeFilter.ALL,
    statusFilter: TransportOrderStatusFilter.ALL,
    sortCriteria: undefined,
}

interface SuccessFetchTransportOrderBundlesPayload {
    totalItems: number
    items: TransportOrder[]
}

export const transportOrderSlice = createSlice({
    name: "transportOrder",
    initialState,
    reducers: {
        setTransportOrderBundles: (state, action: PayloadAction<SuccessFetchTransportOrderBundlesPayload>) => {
            state.transportOrders = action.payload.items
            state.failedFetchTransportOrderId = undefined
            state.overviewTransportOrders = getOverviewTransportOrders(
                state.transportOrders,
                state.searchPhrase,
                state.cargoTypeFilter,
                state.statusFilter,
                state.sortCriteria)
        },
        sortTransportOrderBundles: (state, action: PayloadAction<SortCriteria | undefined>) => {
            state.sortCriteria = action.payload
            state.overviewTransportOrders = getOverviewTransportOrders(
                state.transportOrders,
                state.searchPhrase,
                state.cargoTypeFilter,
                state.statusFilter,
                state.sortCriteria)
        },
        searchTransportOrderBundles: (state, action: PayloadAction<string | undefined>) => {
            state.searchPhrase = action.payload
            state.overviewTransportOrders = getOverviewTransportOrders(
                state.transportOrders,
                state.searchPhrase,
                state.cargoTypeFilter,
                state.statusFilter,
                state.sortCriteria)
        },
        filterTransportOrderBundlesByCargoType: (state, action: PayloadAction<CargoTypeFilter>) => {
            state.cargoTypeFilter = action.payload
            state.overviewTransportOrders = getOverviewTransportOrders(
                state.transportOrders,
                state.searchPhrase,
                state.cargoTypeFilter,
                state.statusFilter,
                state.sortCriteria)
        },
        filterTransportOrderBundlesByStatus: (state, action: PayloadAction<TransportOrderStatusFilter>) => {
            state.statusFilter = action.payload
            state.overviewTransportOrders = getOverviewTransportOrders(
                state.transportOrders,
                state.searchPhrase,
                state.cargoTypeFilter,
                state.statusFilter,
                state.sortCriteria)
        },
    },
})
